import React, { useState } from 'react'
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons'
import { IFaxFolder, IFaxMessage, IFaxTag } from '@dataTypes/fax.type'
import type { FaxPreviewHooks } from './useFaxPreview'
import MoveFaxesModal from './modals/MoveFaxesModal.component'
import replyIcon from '@assets/images/Messages/reply.svg'
import forwardIcon from '@assets/images/Messages/forward.svg'

type Props = {
  fax: IFaxMessage
  hooks: FaxPreviewHooks
  folders: IFaxFolder[]
  tags: IFaxTag[]
  canReplyForward: boolean
  onReply: () => void
  onForward: () => void
  onPrint: () => void
}

const FaxDetailToolbar: React.FC<Props> = ({
  fax,
  hooks,
  folders,
  tags,
  canReplyForward,
  onReply,
  onForward,
  onPrint,
}) => {
  const { capabilities } = hooks
  const [showMoveModal, setShowMoveModal] = useState(false)
  const readIcon = fax.is_read ? 'mgc_mail_open_fill' : 'mgc_mail_fill'
  const readTitle = fax.is_read ? 'Mark as unread' : 'Mark as read'
  const showBlock = hooks.showBlockAction(fax)

  const runDelete = (): void => {
    hooks.handleMultipleSelectAction(
      hooks.activeFolder === 'trash' ? 'permanently_delete' : 'delete',
      [fax],
    )
  }

  const runArchive = (): void => {
    hooks.handleMultipleSelectAction(
      capabilities.archiveActionIsUnarchive ? 'unarchive' : 'archive',
      [fax],
    )
  }

  const runRecover = (): void => {
    hooks.handleRecoverSelected()
  }

  const runReadToggle = (): void => {
    hooks.handleToggleReadSelected()
  }

  const runBlock = (): void => {
    hooks.openBlockModalForFax(fax)
  }

  const runMoveToFolder = (folderId: string): void => {
    hooks.handleMoveToFolder(folderId)
  }

  return (
    <>
      <div className="fax-detail-toolbar">
        <div className="fax-detail-toolbar-group">
          {capabilities.showDelete && (
            <button
              type="button"
              className="fax-detail-toolbar-btn"
              title="Delete"
              aria-label="Delete"
              onClick={runDelete}
            >
              <i className="mgc_delete_2_fill" />
            </button>
          )}
          {capabilities.showRefresh && (
            <button
              type="button"
              className="fax-detail-toolbar-btn"
              title="Refresh"
              aria-label="Refresh"
              onClick={hooks.handleRefresh}
            >
              <i className="mgc_refresh_1_line" />
            </button>
          )}
          {capabilities.showRecover && (
            <button
              type="button"
              className="fax-detail-toolbar-btn"
              title="Restore to inbox"
              aria-label="Restore to inbox"
              onClick={runRecover}
            >
              <i className="mgc_back_line" />
            </button>
          )}
          {capabilities.showReadToggle && (
            <button
              type="button"
              className="fax-detail-toolbar-btn"
              title={readTitle}
              aria-label={readTitle}
              onClick={runReadToggle}
            >
              <i className={readIcon} />
            </button>
          )}
          {capabilities.showArchiveAction && (
            <button
              type="button"
              className="fax-detail-toolbar-btn"
              title={
                capabilities.archiveActionIsUnarchive ? 'Unarchive' : 'Archive'
              }
              aria-label={
                capabilities.archiveActionIsUnarchive ? 'Unarchive' : 'Archive'
              }
              onClick={runArchive}
            >
              <i className="mgc_archive_fill" />
            </button>
          )}
          {showBlock && (
            <button
              type="button"
              className="fax-detail-toolbar-btn"
              title="Block user"
              aria-label="Block user"
              onClick={runBlock}
            >
              <i className="mgc_alert_fill" />
            </button>
          )}
          {capabilities.showMoveToFolder && (
            <button
              type="button"
              className="fax-detail-toolbar-btn"
              title="Move to folder"
              aria-label="Move to folder"
              onClick={() => setShowMoveModal(true)}
            >
              <i className="mgc_folder_fill" />
            </button>
          )}
          {capabilities.showApplyLabel && (
            <span className="fax-detail-toolbar-tag-wrap">
              <DropDownButtonComponent
                items={tags.map((tag) => ({
                  text: tag.tag_name,
                  id: tag.fax_tag_id!,
                }))}
                cssClass="e-caret-hide fax-detail-toolbar-tag-btn"
                iconCss="mgc_tag_2_fill"
                title="Apply label"
                select={(args) => {
                  const id = args.item.id as string | undefined
                  if (id) hooks.onSelectTag(id)
                }}
              />
            </span>
          )}
        </div>
        <div className="fax-detail-toolbar-group fax-detail-toolbar-group-right">
          {canReplyForward && (
            <>
              <button
                type="button"
                className="fax-detail-toolbar-btn"
                title="Reply"
                aria-label="Reply"
                onClick={onReply}
              >
                <img
                  src={replyIcon}
                  alt=""
                  className="fax-detail-toolbar-icon"
                  aria-hidden
                />
              </button>
              <button
                type="button"
                className="fax-detail-toolbar-btn"
                title="Forward"
                aria-label="Forward"
                onClick={onForward}
              >
                <img
                  src={forwardIcon}
                  alt=""
                  className="fax-detail-toolbar-icon"
                  aria-hidden
                />
              </button>
            </>
          )}
          <button
            type="button"
            className="fax-detail-toolbar-btn"
            title="Print"
            aria-label="Print"
            onClick={onPrint}
          >
            <i className="mgc_print_fill" />
          </button>
        </div>
      </div>
      <MoveFaxesModal
        show={showMoveModal}
        onHide={() => setShowMoveModal(false)}
        folders={folders}
        onMove={runMoveToFolder}
      />
    </>
  )
}

export default FaxDetailToolbar
